import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Form, notification, Spin } from 'antd'
import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { PermissionForm } from '@/components/form/PermissionForm'
import type { PermissionFormValues } from '@/components/form/PermissionForm'

import {
  createPermission,
  updatePermission,
  getPermissionById,
} from '@/api/permissions.api'
import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'

interface PermissionFormDrawerProps {
  isOpen: boolean
  mode: 'create' | 'update'
  permissionId: string | null
  onClose: () => void
}

export function PermissionFormDrawer({
  isOpen,
  mode,
  permissionId,
  onClose,
}: PermissionFormDrawerProps) {
  const [form] = Form.useForm<PermissionFormValues>()
  const queryClient = useQueryClient()
  const currentUser = useAtomValue(currentUserAtom)

  const { data: permission, isLoading: isFetching } = useQuery({
    queryKey: ['permission-detail', permissionId],
    queryFn: ({ signal }) =>
      permissionId
        ? getPermissionById(permissionId, signal)
        : Promise.resolve(null),
    enabled: isOpen && mode === 'update' && !!permissionId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  useEffect(() => {
    if (isOpen) {
      if (mode === 'update' && permission) {
        // Lấy ID các quyền đang được tick
        const checkedPermissions = permission.categoryPermissionList
          .flatMap((cat) => cat.permissionList)
          .filter((p) => p.isSelected)
          .map((p) => p.id)

        // Lấy ID các đơn vị đang được áp dụng
        const selectedUnits = permission.unitList.map((u) => u.id)

        form.setFieldsValue({
          id: permission.id,
          code: permission.code,
          name: permission.name,
          appliedUnit: selectedUnits,
          note: permission.description || '',
          permissions: checkedPermissions,
        })
      } else if (mode === 'create') {
        form.resetFields()
        form.setFieldValue(
          'appliedUnit',
          currentUser?.unitId ? [String(currentUser.unitId)] : [],
        )
      }
    } else {
      form.resetFields()
    }
  }, [isOpen, mode, permission, form, currentUser?.unitId])

  const createMutation = useMutation({
    mutationFn: createPermission,
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Thêm mới nhóm quyền thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['permissions'] })
      onClose()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (values: any) => {
      if (!permissionId) throw new Error('Missing permission ID')
      return updatePermission(permissionId, values)
    },
    onSuccess: (response) => {
      const successMessage =
        response.responseMessage || 'Cập nhật nhóm quyền thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['permissions'] })
      onClose()
    },
  })

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const isDrawerDisabled = isSubmitting || isFetching

  const handleSubmit = (values: PermissionFormValues) => {
    const unitIds = Array.isArray(values.appliedUnit) ? values.appliedUnit : []
    const permIds = Array.isArray(values.permissions) ? values.permissions : []
    const currentUserUnitId = currentUser?.unitId
      ? [String(currentUser.unitId)]
      : unitIds

    if (mode === 'create') {
      createMutation.mutate({
        name: values.name,
        unitId: currentUserUnitId,
        description: values.note || '',
        permissionIds: permIds as string[],
      })
    } else {
      const oldUnitIds = permission ? permission.unitList.map((u) => u.id) : []
      const oldPermIds = permission
        ? permission.categoryPermissionList
            .flatMap((c) => c.permissionList)
            .filter((p) => p.isSelected)
            .map((p) => p.id)
        : []
      const currentStatus = permission ? permission.status : 1

      updateMutation.mutate({
        name: values.name,
        newUnitIds: oldUnitIds,
        oldUnitIds: oldUnitIds,
        description: values.note || '',
        newPermissionIds: permIds as string[],
        oldPermissionIds: oldPermIds,
        status: currentStatus,
      })
    }
  }

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'THÊM MỚI NHÓM QUYỀN' : 'CẬP NHẬT NHÓM QUYỀN'}
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => form.submit()}
            disabled={isDrawerDisabled}
            className="min-w-[160px] h-10"
          >
            {isSubmitting ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DrawerFooter>
      }
    >
      <Spin spinning={isFetching} description="Đang tải dữ liệu...">
        <div className="min-h-[300px]">
          {(mode === 'create' || permission) && (
            <PermissionForm
              form={form}
              unitFieldDisabled={mode === 'update'}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </Spin>
    </Drawer>
  )
}
